import {
  type AuditResource,
  createCodeableConcept,
  createCoding,
  createReference,
  createReferenceFromResource,
  DiagnosisStatusCode,
  DiagnosisTypeCode,
  getCodes,
  isValidReference,
  medpraxSystem,
  type RequiredCoding,
  type WithId,
} from '@quromedical/fhir-common';
import { compact, first, uniqby } from '@quromedical/utils';
import type {
  CodeableConcept,
  Condition,
  Encounter,
  EncounterDiagnosis,
  Patient,
} from 'fhir/r4';
import type { CreateAdmissionDiagnosis } from '../admission/schema';
import type { MedpraxPageResult } from '../external-integrations';

import type { CreateDiagnosis, Diagnosis } from './schema';

export interface ConditionMedpraxCodes {
  condition: RequiredCoding;
  type: DiagnosisTypeCode;
}
export const mapModelToFhirDiagnosis = (
  diagnosis: Diagnosis,
): WithId<fhir4.Condition> => {
  const { encounter, subject, code, id, category, status } = diagnosis;

  return {
    resourceType: 'Condition',
    id,
    subject,
    code,
    encounter,
    clinicalStatus: status,
    category: [category],
  };
};

export const mapCreateDiagnosisToFhir = (
  diagnosis: CreateDiagnosis,
): fhir4.Condition => {
  const { code, encounter, subject, category, status } = diagnosis;
  return {
    resourceType: 'Condition',
    subject,
    encounter,
    code,
    clinicalStatus: status,
    category: [category],
  };
};

export const mapICD10ToCoding = (
  codes: MedpraxPageResult[] = [],
): RequiredCoding[] =>
  codes.map((code) => createCoding(code.Code, code.Description, medpraxSystem));

const getCodeableConceptCode = (code?: CodeableConcept) => {
  const coding = code ? first(code.coding) : undefined;
  const codes = coding ? coding.code : undefined;
  const display = coding ? coding.display : undefined;
  const codeableConceptCode = codes
    ? createCodeableConcept(codes, codes, display)
    : undefined;

  return codeableConceptCode;
};
export const mapFhirToDiagnosisModel = (
  fhir: WithId<fhir4.Condition>,
): Partial<Diagnosis> => {
  const { code, subject, encounter, id, category, clinicalStatus } = fhir;
  const subjectReference = isValidReference(subject) ? subject : undefined;
  const encounterReference = isValidReference(encounter)
    ? encounter
    : undefined;

  const getCode = getCodeableConceptCode(code);
  const getCategory = getCodeableConceptCode(first(category));
  const getClinicalStatus = getCodeableConceptCode(clinicalStatus);

  return {
    id,
    code: getCode,
    encounter: encounterReference,
    subject: subjectReference,
    status: getClinicalStatus,
    category: getCategory,
  };
};

export const resolveConditionDifference = (
  patient: WithId<Patient>,
  encounter: WithId<Encounter>,
  diagnoses: ConditionMedpraxCodes[],
  conditions: WithId<Condition>[],
) => {
  const newConditions = [];
  const currentConditions = [];

  for (const diagnosis of diagnoses) {
    const isDiagnosis = conditions.some((condition) =>
      isSameDiagnosis(diagnosis, condition),
    );

    if (!isDiagnosis) {
      newConditions.push(diagnosis);
    }
  }

  for (const condition of conditions) {
    const isCondition = diagnoses.some((diagnosis) =>
      isSameDiagnosis(diagnosis, condition),
    );

    if (isCondition) {
      currentConditions.push(condition);
    }
  }

  const createCondition = newConditions.map((conditions) =>
    mapCreateDiagnosisToFhir(createDiagnoses(patient, encounter, conditions)),
  );

  const updatedConditions = compact(
    conditions.map((condition): WithId<Condition> | undefined => {
      const isCondition = createCondition.find((diagnosis) => {
        const hasCondition = isConditionType(diagnosis, condition);

        if (hasCondition) {
          createCondition.splice(createCondition.indexOf(diagnosis), 1);
        }
        return hasCondition;
      });

      const updateConditionCode = {
        ...condition,
        code: isCondition?.code,
        clinicalStatus: createCodeableConcept(DiagnosisStatusCode.active),
      };

      const updateConditionStatus = {
        ...condition,
        clinicalStatus: createCodeableConcept(DiagnosisStatusCode.inactive),
      };

      return isCondition?.code ? updateConditionCode : updateConditionStatus;
    }),
  );
  const mapActiveInActiveConditions = compact(
    conditions.map((condition) => {
      const isCondition = diagnoses.some((diagnosis) =>
        isSameDiagnosis(diagnosis, condition),
      );
      return isCondition
        ? {
            ...condition,
            clinicalStatus: createCodeableConcept(DiagnosisStatusCode.active),
          }
        : undefined;
    }),
  );

  const mergeActiveInActiveConditions = [
    ...mapActiveInActiveConditions,
    ...updatedConditions,
  ];

  const resolveConditions = conditions.filter((current) => {
    const updatedCondition = !mergeActiveInActiveConditions.some(
      (updatedCondition) => updatedCondition.id === current.id,
    );

    return updatedCondition;
  });

  const updatedCondition = [
    ...resolveConditions,
    ...mergeActiveInActiveConditions,
  ];

  const uniqueUpdatedCondition = uniqby(updatedCondition, 'id');

  return {
    postCondition: createCondition,
    putCondition: uniqueUpdatedCondition,
  };
};

export const isSameDiagnosis = (
  diagnosis: ConditionMedpraxCodes,
  condition: WithId<Condition>,
): boolean => {
  const conditionCode =
    diagnosis.condition.code === first(getCodes(condition.code));
  const conditionType =
    diagnosis.type === first(getCodes(first(condition.category)));
  return conditionCode && conditionType;
};

export const isConditionType = (
  diagnosis: Condition,
  condition: WithId<Condition>,
): boolean => {
  const conditionType =
    first(getCodes(first(diagnosis?.category))) ===
    first(getCodes(first(condition.category)));
  const conditionCode =
    first(getCodes(diagnosis?.code)) === first(getCodes(condition?.code));

  return conditionType && conditionCode;
};
const createDiagnoses = (
  patient: WithId<Patient>,
  encounter: WithId<Encounter>,
  diagnoses: ConditionMedpraxCodes,
) => {
  return {
    code: createCodeableConcept(
      diagnoses.condition.code,
      diagnoses.condition.display,
      diagnoses.condition.display,
      diagnoses.condition.system,
    ),
    subject: createReferenceFromResource(patient),
    encounter: createReferenceFromResource(encounter),
    status: createCodeableConcept(DiagnosisStatusCode.active),
    category: createCodeableConcept(diagnoses.type as string),
  };
};

export const mapConditionToDiagnoses = (
  conditions: Required<AuditResource<Condition>>[],
): EncounterDiagnosis[] => {
  return conditions.map((c) => {
    return {
      condition: {
        reference: c.resource.id
          ? createReference('Condition', c.resource.id).reference
          : c.fullUrl,
        display: `${first(getCodes(c.resource?.code))}: ${c.resource.code?.text}`,
      },
      use: first(c.resource.category),
    };
  });
};

export const mapDiagnosesToMedprax = (
  diagnosesCode: CreateAdmissionDiagnosis[],
  diagnosesIcd10CodesMedprax: RequiredCoding[],
): ConditionMedpraxCodes[] => {
  const mapConditionToDiagnosis =
    diagnosesIcd10CodesMedprax.map<ConditionMedpraxCodes>((medpraxCode) => {
      const diagnoses = diagnosesCode.find((diagnosis) => {
        const isDiagnosisCode = diagnosis.condition === medpraxCode.code;
        if (isDiagnosisCode) {
          diagnosesCode.splice(diagnosesCode.indexOf(diagnosis), 1);
        }
        return isDiagnosisCode;
      });

      return {
        condition: medpraxCode,
        type: diagnoses?.type as DiagnosisTypeCode,
      };
    });
  return mapConditionToDiagnosis;
};

export const mapActiveConditions = (
  diagnosis: Required<AuditResource<Condition>>[],
): Required<AuditResource<Condition>>[] => {
  const diagnoses = diagnosis.filter(
    (d) =>
      first(getCodes(d.resource.clinicalStatus)) === DiagnosisStatusCode.active,
  );
  return compact(diagnoses);
};

export const mapInactiveConditions = (
  diagnosis: Required<AuditResource<Condition>>[],
): Required<AuditResource<Condition>>[] => {
  const diagnoses = diagnosis.filter(
    (d) =>
      first(getCodes(d.resource.clinicalStatus)) ===
      DiagnosisStatusCode.inactive,
  );
  return compact(diagnoses);
};

export const findReasonOfAdmission = (
  diagnosesIcd10CodesMedprax: RequiredCoding[],
  diagnoses: CreateAdmissionDiagnosis[],
) => {
  const rfaDiagnosisIcd10Code = diagnoses?.find(
    (a) => a.type === DiagnosisTypeCode.rfa,
  );
  return diagnosesIcd10CodesMedprax.find(
    (c) => c.code === rfaDiagnosisIcd10Code?.condition,
  );
};
